// Utility function to normalize locale paths
function normalizeLocalePath(loc) {
  if (loc == null) return "";
  const raw = String(loc).trim();
  if (!raw) return "";

  // Guard against accidentally storing a scheme like "https:" (would become https://products/...)
  // or a full URL. In those cases we only keep the pathname.
  if (/^https?:/i.test(raw)) {
    try {
      const u = new URL(raw);
      return normalizeLocalePath(u.pathname);
    } catch {
      return "";
    }
  }

  // Ensure we only end up with a locale segment like "/en-dk" (or empty string).
  const cleaned = raw.replace(/^\/+|\/+$/g, "");
  if (!cleaned) return "";
  // reject multi-segment paths or scheme-like strings to ensure only a single locale segment like 'en-dk' is accepted
  if (cleaned.includes("/") || cleaned.includes(":")) return "";
  return `/${cleaned}`;
}

class ProductConfigurator extends HTMLElement {
  constructor() {
    super();

    // default values
    this.selected = {
      frame: null,
      frameMaterial: null,
      mattress: null,
      size: null,
      addons: null,
    };

    // swatches
    this.frameSwatches = [];
    this.frameMaterialSwatches = [];
    this.mattressSwatches = [];
    this.addonSwatches = [];
    this.sizeSwatches = [];
    this.localization;

    // product data
    this.productData = null;

    // NEW: Bind the resize event to a debounced or direct function
    this.onResize = this.checkOverflow.bind(this);
  }

  hasFrameMaterialOption() {
    if (Array.isArray(this.frameMaterialSwatches) && this.frameMaterialSwatches.length > 0) return true;
    if (this.productData && Array.isArray(this.productData.connectedProducts)) {
      return this.productData.connectedProducts.some((p) => (p?.frameMaterial ?? "") !== "");
    }
    return false;
  }

  normalizeConnectedProduct(p) {
    return {
      raw: p,
      frame: p?.frame?.frameColor ?? "",
      frameMaterial: p?.frameMaterial ?? "",
      mattress: p?.mattress?.mattressColor ?? "",
      size: p?.size ?? "",
      addons: p?.selectedAddons ?? "",
    };
  }

  /**
   * Finds the "best" existing connected product for the current selection.
   * - Keeps the changed option locked (must match)
   * - Prefers keeping the rest of the current selections (size/color/etc) when possible
   * - Falls back to the first available combination when some options/sizes don't exist
   */
  findBestMatchingConnectedProduct({ changedKey } = {}) {
    if (!this.productData || !Array.isArray(this.productData.connectedProducts)) return null;

    const s = this.selected ?? {};
    const required = {};
    const hasFrameMaterial = this.hasFrameMaterialOption();

    // Lock the option the user just changed (or lock anything that's already set if no key provided)
    if (changedKey) {
      // If this product doesn't support frame material, ignore it entirely.
      if (changedKey === "frameMaterial" && !hasFrameMaterial) {
        // no-op
      } else {
        required[changedKey] = s[changedKey] ?? "";
      }

      // When materials exist, frame color availability depends on the selected material.
      // So a frame color change should keep the current material locked (if set).
      if (changedKey === "frame" && hasFrameMaterial && s.frameMaterial != null) {
        required.frameMaterial = s.frameMaterial ?? "";
      }
    } else {
      ["frame", "frameMaterial", "mattress", "size", "addons"].forEach((k) => {
        if (k === "frameMaterial" && !hasFrameMaterial) return;
        if (s[k] != null) required[k] = s[k];
      });
    }

    // Weights express "how much we want to keep" an option when multiple combos exist.
    // Size tends to have the biggest availability gaps (e.g. oak), so we strongly prefer it when it exists.
    const weights = { size: 5, frame: 4, frameMaterial: 4, mattress: 3, addons: 1 };

    let best = null;
    let bestScore = -Infinity;

    for (const p of this.productData.connectedProducts) {
      const n = this.normalizeConnectedProduct(p);

      // Must satisfy the locked requirement (changed option)
      let ok = true;
      for (const [k, v] of Object.entries(required)) {
        const desired = (v ?? "") + "";
        if ((n[k] ?? "") + "" !== desired) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;

      // Score by how many of the *other* current selections we can keep
      let score = 0;
      for (const k of ["frame", "frameMaterial", "mattress", "size", "addons"]) {
        if (k === "frameMaterial" && !hasFrameMaterial) continue;
        const current = s[k];
        if (current == null) continue;
        if ((n[k] ?? "") + "" === (current ?? "") + "") score += weights[k] ?? 0;
      }

      if (score > bestScore) {
        bestScore = score;
        best = p;
      }
    }

    return best;
  }

  syncSelectedFromConnectedProduct(match) {
    if (!match) return;
    const n = this.normalizeConnectedProduct(match);
    this.selected.frame = n.frame || null;
    this.selected.frameMaterial = n.frameMaterial || null;
    this.selected.mattress = n.mattress || null;
    this.selected.size = n.size || null;
    this.selected.addons = n.addons || null;
  }

  connectedCallback() {
    // initialize from global productData if available
    if (typeof window !== "undefined" && window.productData && typeof window.productData === "object") {
      this.productData = window.productData;
      this.selected.frame = this.productData.selectedFrame;
      this.selected.frameMaterial = this.productData.selectedFrameMaterial;
      this.selected.mattress = this.productData.selectedMattress;
      this.selected.size = this.productData.selectedSize;
      this.selected.addons = this.productData.selectedAddons;
      this.frameSwatches = this.productData.frameColorObjects;
      this.frameMaterialSwatches = this.productData.frameMaterialObjects;
      this.mattressSwatches = this.productData.mattressColorObjects;
      this.sizeSwatches = this.productData.sizeObjects;
      this.addonSwatches = this.productData.addonObjects;
      this.localization = this.productData.localization;

      this.createOptions();
      this.updateAllSwatchAvailabilities();
    }

    // We use requestAnimationFrame or setTimeout to ensure DOM is rendered
    requestAnimationFrame(() => {
      this.checkOverflow();
    });

    // NEW: Add resize listener
    window.addEventListener("resize", this.onResize);

    const self = this;
    document.addEventListener("click", function (event) {
      // Selectors for the boundaries and the target element
      const containerSelector = ".product-configurator-option";
      const excludedValueAreaSelector = ".product-configurator-option__item-values";
      const targetButtonSelector = ".product-configurator-option__item-value-drawer__toggle";

      // 1. Check if the click occurred inside the main container
      const isInsideContainer = event.target.closest(containerSelector);

      // 2. Check if the click occurred inside the TWO excluded areas:
      // a) The list of item values
      const isInsideValueArea = event.target.closest(excludedValueAreaSelector);

      // b) The toggle button itself
      // We check the target *itself* and its ancestors against the toggle selector.
      const isToggle = event.target.closest(targetButtonSelector);
      const optionName = isInsideContainer?.dataset?.optionName;

      // If the click is:
      // 1. Inside the main container (isInsideContainer is true)
      // AND
      // 2. NOT inside the value area (isInsideValueArea is null)
      // AND
      // 3. NOT on the toggle button (isToggle is null)
      if (isInsideContainer && !isInsideValueArea && !isToggle) {
        // Find the specific toggle button within the clicked container
        const containerElement = isInsideContainer;
        const toggleButton = containerElement.querySelector(targetButtonSelector);

        // Ensure the toggle button exists before trying to click it
        if (toggleButton) {
          // Trigger the click action on the toggle button
          toggleButton.click();

          // Mattress drawer: re-measure sticky offset after opening
          if (optionName === "Mattress color") {
            requestAnimationFrame(() => self.updateMattressColorFiltersStickyTop());
          }
        }
      }

      // If the user clicked the toggle button directly, it won't run the "programmatic click" path above.
      if (isInsideContainer && !isInsideValueArea && isToggle && optionName === "Mattress color") {
        requestAnimationFrame(() => self.updateMattressColorFiltersStickyTop());
      }
    });
  }

  // NEW: Cleanup listener when element is removed
  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
  }

  checkOverflow() {
    const wrappers = this.querySelectorAll(".product-configurator-option__item-value-wrapper");

    wrappers.forEach((wrapper) => {
      const container = wrapper.querySelector(".product-configurator-option__item-values");
      if (!container) return;

      // 1. Select all direct children (the items)
      const items = Array.from(container.querySelectorAll(".product-configurator-option__drawer-item-value"));

      // 2. RESET: Remove hidden class and existing counters to measure true natural width
      items.forEach((item) => item.classList.remove("is-overflow-hidden"));

      // 3. Measure
      const wrapperWidth = wrapper.getBoundingClientRect().width;
      // We use a small buffer (2px) to handle browser sub-pixel rendering differences
      const maxRight = wrapper.getBoundingClientRect().left + wrapperWidth;

      let hiddenCount = 0;

      items.forEach((item) => {
        const itemRect = item.getBoundingClientRect();

        // If the right edge of the item is past the right edge of the wrapper
        // It is either cut off or fully overflowing
        if (itemRect.right > maxRight) {
          item.classList.add("is-overflow-hidden");
          hiddenCount++;
        }
      });

      // 4. Add Counter if needed
      if (hiddenCount > 0) {
        const nextSibling = wrapper.nextElementSibling;
        // 4. Check if the sibling exists before setting HTML
        if (nextSibling) {
          nextSibling.innerHTML = `+${hiddenCount}`;
        }
      } else {
        wrapper.nextSibling.innerHTML = "";
      }
    });

    // Mattress drawer (mobile): keep filters pinned directly under the sticky image.
    this.updateMattressColorFiltersStickyTop();
  }

  updateMattressColorFiltersStickyTop() {
    // Only for mobile
    const isDesktop = window.matchMedia("(min-width: 990px)").matches;

    const drawer = document.querySelector("#product-options-mattress-color");
    if (!drawer) return;

    const imageWrapper = drawer.querySelector(".product-configurator-option__drawer-main-image-wrapper");
    const filters = drawer.querySelector(".product-configurator-option__drawer-item-values__filters");
    if (!imageWrapper || !filters) return;

    if (isDesktop) {
      // Remove any previously-set inline top when switching back to desktop.
      filters.style.top = "";
      return;
    }

    const imageHeight = imageWrapper.getBoundingClientRect().height;
    if (!Number.isFinite(imageHeight) || imageHeight <= 0) return;

    // Sticky element: set `top` so it starts exactly after the sticky image.
    filters.style.top = `${imageHeight}px`;
  }

  createOptions() {
    // create options and drawers from productData
    if (this.frameSwatches && this.frameSwatches.length > 0) {
      this.populateSwatches("Frame color", this.frameSwatches, "frame");
    }
    if (this.frameMaterialSwatches && this.frameMaterialSwatches.length > 0) {
      this.populateSwatches("Frame material", this.frameMaterialSwatches, "frameMaterial");
    }
    if (this.mattressSwatches && this.mattressSwatches.length > 0) {
      this.populateSwatches("Mattress color", this.mattressSwatches, "mattress");
    }
    if (this.sizeSwatches && this.sizeSwatches.length > 0) {
      this.populateSwatches("Size", this.sizeSwatches, "size");
    }
    if (this.addonSwatches && this.addonSwatches.length > 0) {
      this.populateSwatches("Addons", this.addonSwatches, "addons");
    }

    this.updateSelectedOnClick();
    this.updateAllSwatchAvailabilities();

    // NEW: Check overflow after creating options
    requestAnimationFrame(() => {
      this.checkOverflow();
    });
  }

  populateSwatches(optionType, swatches, optionName) {
    // populate swatches from productData
    const target = document.querySelector(`[data-option-name="${optionType}"] .product-configurator-option__item-value-wrapper .product-configurator-option__item-values`);
    const drawerTarget = document.querySelector(`[data-option-name="${optionType}"] .product-configurator-option__drawer-item-values`);

    if (optionName == "size") {
      swatches.sort((a, b) => {
        // Split by ' x ' and convert to numbers
        // If there is no ' x ', partsA[1] will simply be undefined
        const partsA = a.split(" x ").map(Number);
        const partsB = b.split(" x ").map(Number);

        // 1. Compare the first number (Width)
        if (partsA[0] !== partsB[0]) {
          return partsA[0] - partsB[0];
        }

        // 2. Compare the second number (Length)
        // The "|| 0" ensures that if the second number is missing, we don't get an error
        return (partsA[1] || 0) - (partsB[1] || 0);
      });
    }

    swatches.forEach((swatch) => {
      let cloneTemplate;
      if (optionName == "size") {
        cloneTemplate = document.querySelector("#product-configurator-option-drawer-item-value-size-template").content.cloneNode(true);
      } else {
        cloneTemplate = document.querySelector("#product-configurator-option-drawer-item-value-template").content.cloneNode(true);
      }

      if (optionName == "addons") {
        const clone = cloneTemplate.querySelector(".product-configurator-option__drawer-item-value");
        clone.dataset.value = swatch.addonValue;
        const img = clone.querySelector("img");
        const span = clone.querySelector("span");
        img.src = swatch.addon_img;
        img.alt = swatch.addonValue;
        if (clone.dataset.value == this.selected[optionName]) {
          clone.classList.add("swatch-active");
        }
        span.textContent = swatch.addonValue;

        const cloneDrawer = clone.cloneNode(true);
        cloneDrawer.classList.add("drawer-swatch");
        cloneDrawer.dataset.filterValues = swatch.color_material_name;

        // populate swatches and drawer
        target.appendChild(clone);
        drawerTarget.appendChild(cloneDrawer);
      } else if (optionName == "size") {
        const clone = cloneTemplate.querySelector(".product-configurator-option__drawer-item-value");
        clone.dataset.value = swatch;
        const div = clone.querySelector("div");
        div.textContent = swatch;
        if (clone.dataset.value == this.selected[optionName]) {
          clone.classList.add("swatch-active");
        }

        const cloneDrawer = clone.cloneNode(true);
        cloneDrawer.classList.add("drawer-swatch");
        cloneDrawer.dataset.filterValues = swatch.size_name;

        // // populate swatches and drawer
        target.appendChild(clone);
        drawerTarget.appendChild(clone);
      } else if (optionName == "frameMaterial") {
        const clone = cloneTemplate.querySelector(".product-configurator-option__drawer-item-value");
        clone.dataset.value = swatch.name;
        const img = clone.querySelector("img");
        const span = clone.querySelector("span");
        img.src = swatch.image;
        img.alt = swatch.name;
        if (clone.dataset.value == this.selected[optionName]) {
          clone.classList.add("swatch-active");
        }
        span.textContent = swatch.name;

        const cloneDrawer = clone.cloneNode(true);
        cloneDrawer.classList.add("drawer-swatch");

        // populate swatches and drawer
        target.appendChild(clone);
        drawerTarget.appendChild(cloneDrawer);
      } else {
        const clone = cloneTemplate.querySelector(".product-configurator-option__drawer-item-value");
        clone.dataset.value = swatch.color_name;
        const img = clone.querySelector("img");
        const span = clone.querySelector("span");
        img.src = swatch.color_image;
        img.alt = swatch.color_name;
        if (clone.dataset.value == this.selected[optionName]) {
          clone.classList.add("swatch-active");
        }
        span.textContent = swatch.color_name;

        const cloneDrawer = clone.cloneNode(true);
        cloneDrawer.classList.add("drawer-swatch");
        cloneDrawer.dataset.filterValues = swatch.color_material_name;

        // populate swatches and drawer
        target.appendChild(clone);
        drawerTarget.appendChild(cloneDrawer);
      }
    });

    if (optionType == "Mattress color") {
      this.materialFilters();
    }
  }

  materialFilters() {
    const mattressDrawer = document.querySelector('.drawer[data-option-name="Mattress color"]');
    const swatches = mattressDrawer.querySelectorAll(".product-configurator-option__drawer-item-value");

    const filterCounts = Array.from(swatches).reduce((acc, swatch) => {
      const value = swatch.getAttribute("data-filter-values");
      if (value) {
        acc[value] = (acc[value] || 0) + 1;
      }

      return acc;
    }, {});

    mattressDrawer.querySelector(".product-configurator-option__drawer-item-values__filters").innerHTML += `<div class="product-configurator-option__drawer-item-value__filter product-configurator-option__drawer-item-value__filter--active " data-filter-values="All">All types (${swatches.length})</div>`;

    Object.entries(filterCounts).forEach(([key, value]) => {
      mattressDrawer.querySelector(".product-configurator-option__drawer-item-values__filters").innerHTML += `<div class="product-configurator-option__drawer-item-value__filter" data-filter-values="${key}">${key} (${value})</div>`;
    });

    mattressDrawer.querySelectorAll(".product-configurator-option__drawer-item-value__filter").forEach((filter) => {
      filter.addEventListener("click", () => {
        mattressDrawer.querySelectorAll(".product-configurator-option__drawer-item-value").forEach((swatch) => {
          if (filter.dataset.filterValues == "All") {
            swatch.classList.remove("swatch-hidden");
          } else {
            if (swatch.dataset.filterValues == filter.dataset.filterValues) {
              swatch.classList.remove("swatch-hidden");
            } else {
              swatch.classList.add("swatch-hidden");
            }
          }
        });

        mattressDrawer.querySelectorAll(".product-configurator-option__drawer-item-value__filter").forEach((filter) => {
          filter.classList.remove("product-configurator-option__drawer-item-value__filter--active");
        });
        filter.classList.add("product-configurator-option__drawer-item-value__filter--active");
      });
    });
  }

  updateAllSwatchAvailabilities() {
    // Safety check
    if (!this.productData || !this.productData.connectedProducts) return;
    const hasFrameMaterial = this.hasFrameMaterialOption();

    // Select all swatches (both in the main view and in the drawers)
    const swatches = this.querySelectorAll(".product-configurator-option__drawer-item-value");

    swatches.forEach((swatch) => {
      // 1. Determine which option (frame, mattress, etc) this swatch belongs to
      const container = swatch.closest("[data-option-name]");
      if (!container) return;

      const optionLabel = container.dataset.optionName;
      let internalKey = null;

      // Map the Display Name to the internal 'this.selected' key
      // keeping logic consistent with your updateSelectedOnClick switch
      switch (optionLabel) {
        case "Frame color":
          internalKey = "frame";
          break;
        case "Frame material":
          internalKey = "frameMaterial";
          break;
        case "Mattress color":
          internalKey = "mattress";
          break;
        case "Size":
          internalKey = "size";
          break;
        case "Addons":
          internalKey = "addons";
          break;
      }

      if (!internalKey) return;

      if (internalKey === "frame") {
        // If materials don't exist for this product, keep old behavior: frame is always selectable.
        if (!hasFrameMaterial) {
          swatch.classList.remove("is-unavailable");
          return;
        }

        // If materials exist, frame availability depends on the currently selected material AND size.
        // Size is the primary selection: discontinued frame colors for the chosen size must be disabled.
        // When a product has no size option, selectedSize stays empty and the size constraint is skipped.
        const selectedMaterial = this.selected?.frameMaterial ?? "";
        const selectedSize = this.selected?.size ?? "";
        const isCompatibleForMaterial = this.productData.connectedProducts.some((p) => (p.frame?.frameColor ?? "") === (swatch.dataset.value ?? "") && (p.frameMaterial ?? "") === selectedMaterial && (!selectedSize || (p.size ?? "") === selectedSize));

        if (isCompatibleForMaterial) {
          swatch.classList.remove("is-unavailable");
        } else {
          swatch.classList.add("is-unavailable");
        }
        return;
      }

      // Materials must always be clickable so users can switch material anytime.
      // We still resolve to a valid connected-product combination after click.
      if (internalKey === "frameMaterial" && hasFrameMaterial) {
        swatch.classList.remove("is-unavailable");
        return;
      }

      // 2. Create a "Hypothetical" selection
      // Start with current selection, but overwrite the key for this specific swatch
      const testSelection = {
        ...this.selected,
        [internalKey]: swatch.dataset.value,
      };

      // 3. Check if this hypothetical combination exists in your data
      const isCompatible = this.productData.connectedProducts.some((p) => (p.frame?.frameColor ?? "") === (testSelection.frame ?? "") && (!hasFrameMaterial || (p.frameMaterial ?? "") === (testSelection.frameMaterial ?? "")) && (p.mattress?.mattressColor ?? "") === (testSelection.mattress ?? "") && (p.size ?? "") == (testSelection.size ?? "") && (p.selectedAddons ?? "") === (testSelection.addons ?? ""));

      // 4. Toggle the opacity class
      if (isCompatible) {
        swatch.classList.remove("is-unavailable");
      } else {
        swatch.classList.add("is-unavailable");
      }
    });
  }

  // Helper to update active swatches based on current state (this.selected)
  updateActiveSwatches() {
    const map = { frame: "Frame color", frameMaterial: "Frame material", mattress: "Mattress color", size: "Size", addons: "Addons" };

    Object.keys(this.selected).forEach((key) => {
      const val = this.selected[key];
      const name = map[key];
      if (!val || !name) return;

      // Find containers (main view and drawers)
      const containers = this.querySelectorAll(`[data-option-name="${name}"]`);
      containers.forEach((container) => {
        // Reset active
        container.querySelectorAll(".swatch-active").forEach((el) => el.classList.remove("swatch-active"));
        // Set new active
        const target = container.querySelector(`[data-value="${val}"]`);
        if (target) {
          target.classList.add("swatch-active");

          // Also update the text label above the swatches if it exists
          const labelSpan = container.querySelector(".product-configurator-option__item-name span");
          if (labelSpan) labelSpan.textContent = val;
        }
      });
    });
  }

  updateSelectedOnClick() {
    document.querySelectorAll(".product-configurator-option__drawer-item-value").forEach((swatch) => {
      swatch.addEventListener("click", () => {
        const optionName = swatch.closest(".product-configurator-option, .drawer").dataset.optionName;
        let optionNameInternal = optionName;
        const optionValue = swatch.dataset.value;
        switch (optionName) {
          case "Frame color":
            optionNameInternal = "frame";
            break;
          case "Frame material":
            optionNameInternal = "frameMaterial";
            break;
          case "Mattress color":
            optionNameInternal = "mattress";
            break;
          case "Size":
            optionNameInternal = "size";
            break;
          case "Addons":
            optionNameInternal = "addons";
            break;
        }

        // Block unavailable swatches for all options EXCEPT frame color and frame material.
        // Frame material must always be clickable when it exists; frame color is only always clickable
        // on products without materials (old behavior).
        const hasFrameMaterial = this.hasFrameMaterialOption();
        const allowUnavailableClick = optionNameInternal === "frameMaterial" || (optionNameInternal === "frame" && !hasFrameMaterial);

        if (swatch.classList.contains("is-unavailable") && !allowUnavailableClick) {
          return;
        }

        this.selected[optionNameInternal] = optionValue;

        const options = document.querySelectorAll(`[data-option-name="${optionName}"]`);
        options.forEach((option) => {
          if (option.querySelector(".product-configurator-option__item-name")) {
            option.querySelector(".product-configurator-option__item-name span").textContent = optionValue;
          }
          option.querySelector(".swatch-active")?.classList.remove("swatch-active");
          option.querySelector(`[data-value="${optionValue}"]`)?.classList.add("swatch-active");
        });
        swatch.parentElement.querySelector(".swatch-active")?.classList.remove("swatch-active");
        swatch.classList.add("swatch-active");

        // After any change (especially frame material), resolve to the first valid existing combination.
        const match = this.findBestMatchingConnectedProduct({ changedKey: optionNameInternal });
        if (match) {
          this.syncSelectedFromConnectedProduct(match);
          this.updateActiveSwatches();
          this.updateAllSwatchAvailabilities();
          this.updatePageContent(match);
        } else {
          // Fallback to legacy behavior if something unexpected happens
          this.findMatchingConnectedProduct();
        }
      });
    });
  }

  findMatchingConnectedProduct() {
    const s = this.selected;
    const hasFrameMaterial = this.hasFrameMaterialOption();

    // 1. Try to find an exact match based on ALL criteria
    let match = this.productData.connectedProducts.find((p) => (p.frame?.frameColor ?? "") === (s.frame ?? "") && (!hasFrameMaterial || (p.frameMaterial ?? "") === (s.frameMaterial ?? "")) && (p.mattress?.mattressColor ?? "") === (s.mattress ?? "") && (p.size ?? "") == (s.size ?? "") && (p.selectedAddons ?? "") === (s.addons ?? ""));

    if (match) {
      this.updatePageContent(match);
    } else {
      // 2. Fallback: Find match with Frame + Size + Addons (Ignoring Mattress)

      match = this.productData.connectedProducts.find(
        (p) =>
          (p.frame?.frameColor ?? "") === (s.frame ?? "") &&
          (!hasFrameMaterial || (p.frameMaterial ?? "") === (s.frameMaterial ?? "")) &&
          // We skip the mattress check here
          (p.size ?? "") == (s.size ?? "") &&
          (p.selectedAddons ?? "") === (s.addons ?? ""),
      );

      if (match) {
        // UPDATE: Sync state with the found product, because the mattress might have changed
        if (match.mattress?.mattressColor) this.selected.mattress = match.mattress.mattressColor;
        if (match.frameMaterial) this.selected.frameMaterial = match.frameMaterial;
        if (match.size) this.selected.size = match.size;
        if (match.selectedAddons) this.selected.addons = match.selectedAddons;

        // UPDATE: Update the visual swatches to match the new state
        this.updateActiveSwatches();

        // UPDATE: Refresh availabilities based on new state
        this.updateAllSwatchAvailabilities();

        this.updatePageContent(match);
      } else {
        console.log("No matching combination found.");
      }
    }

    return match;
  }

  async updatePageContent(match) {
    if (!match || !match.handle) {
      console.warn("Skipping fetch: No match or match.handle provided.");
      return;
    }

    const localePath = normalizeLocalePath(this.localization);
    const productPath = `${localePath}/products/${match.handle}`;
    const productUrl = new URL(productPath, window.location.origin).toString();

    try {
      const response = await fetch(productUrl, { credentials: "same-origin" });
      if (!response.ok) {
        console.warn(`Fetch failed: ${response.status} ${response.statusText}`);
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const fetchedDoc = parser.parseFromString(html, "text/html");
      const newDrawerImg = fetchedDoc.querySelector(".product-configurator-option__drawer-main-image-wrapper img")?.src;

      if (match.addons.length > 0) {
        let addonImages = document.querySelectorAll('[data-option-name="Addons"] .product-configurator-option__item-values > div img');
        let addonDrawerImages = document.querySelectorAll('x-drawer[data-option-name="Addons"] .product-configurator-option__drawer-item-value img');

        match.addons.forEach((addon, index) => {
          // Check if the image element exists at this index to prevent errors
          if (addonImages[index]) {
            addonImages[index].src = addon.addonImage;
            addonDrawerImages[index].src = addon.addonImage;

            // Optional: Update alt text for accessibility
            addonImages[index].alt = addon.addonValue;
            addonDrawerImages[index].alt = addon.addonValue;
          }
        });
      }

      const mainSelector = "main";
      const fetchedMain = fetchedDoc.querySelector(mainSelector);
      if (!fetchedMain) {
        console.warn(`No <${mainSelector}> found in fetched page: ${productUrl}`);
        return;
      }

      const getDirectSections = (root) => {
        return Array.from(root.children).filter((el) => el.tagName && el.tagName.toLowerCase() === "section");
      };

      let fetchedSections = getDirectSections(fetchedMain);
      if (fetchedSections.length === 0) {
        console.warn("No direct <section> children found in fetched <main>. Nothing to insert.");
        return;
      }

      const currentMain = document.querySelector(mainSelector);
      if (!currentMain) {
        console.warn(`No <${mainSelector}> found in current document.`);
        return;
      }

      const currentSections = getDirectSections(currentMain);

      // ---------------------------------------------------------
      // NEW LOGIC: Recursive Update with "Black Box" Configurator
      // ---------------------------------------------------------

      const updatePreservedSection = (currentNode, newNode) => {
        // 1. Basic validation
        if (!currentNode && newNode) return newNode;
        if (!newNode && currentNode) {
          currentNode.remove();
          return;
        }

        // 2. STOP CONDITION: Is this the Configurator?
        // If yes, we touch NOTHING. We do not replace it, and we do not enter it.
        // We treat the existing node as the absolute source of truth.
        if (currentNode.nodeType === 1 && currentNode.getAttribute("data-block-type") === "product-configurator") {
          return;
        }

        // 3. PARENT CONDITION: Does this node CONTAIN the Configurator?
        // If yes, we must preserve this specific wrapper node, but we still need to
        // check its children to update the siblings of the configurator.
        const hasConfiguratorInside = currentNode.nodeType === 1 && currentNode.querySelector('[data-block-type="product-configurator"]');

        if (hasConfiguratorInside) {
          const currentChildren = Array.from(currentNode.children);
          const newChildren = Array.from(newNode.children);

          const maxLen = Math.max(currentChildren.length, newChildren.length);

          for (let i = 0; i < maxLen; i++) {
            const curChild = currentChildren[i];
            const newChild = newChildren[i];

            if (!curChild && newChild) {
              // New content has an extra child -> append it
              currentNode.appendChild(document.importNode(newChild, true));
            } else if (curChild && !newChild) {
              // New content is missing a child -> remove the old one
              curChild.remove();
            } else {
              // Both exist -> Recurse deeper
              updatePreservedSection(curChild, newChild);
            }
          }
        } else {
          // 4. SAFE ZONE:
          // This node is not the configurator, and it does not contain the configurator.
          // We replace it entirely with the new fetched version.
          if (currentNode.outerHTML !== newNode.outerHTML) {
            currentNode.replaceWith(document.importNode(newNode, true));
          }
        }
      };

      // Execute the recursive update on the first section
      if (currentSections.length > 0 && fetchedSections.length > 0) {
        updatePreservedSection(currentSections[0], fetchedSections[0]);
      }

      // ---------------------------------------------------------
      // EXISTING LOGIC: Handle remaining sections (Index 1+)
      // ---------------------------------------------------------

      // Remove the first section from the fetched list (we handled it manually above)
      fetchedSections = fetchedSections.slice(1);

      // Remove all current sections except the first one (which we preserved/updated)
      let removed = 0;
      if (currentSections.length > 1) {
        for (let i = 1; i < currentSections.length; i++) {
          currentSections[i].remove();
          removed++;
        }
      }

      // Insert remaining fetched sections after the first current section
      const firstCurrent = currentMain.querySelector(":scope > section");
      let insertionBefore = firstCurrent ? firstCurrent.nextSibling : currentMain.firstChild;

      let inserted = 0;
      for (const sec of fetchedSections) {
        const node = document.importNode(sec, true);
        if (insertionBefore) {
          currentMain.insertBefore(node, insertionBefore);
        } else {
          currentMain.appendChild(node);
        }
        inserted++;
      }

      // Reactivate product gallery zoom/lightbox by refreshing the custom element
      const productGallery = document.querySelector("product-gallery");
      if (productGallery && productGallery.parentNode) {
        const galleryClone = productGallery.cloneNode(true);
        productGallery.parentNode.replaceChild(galleryClone, productGallery);
      }

      // NEW: Recalculate overflow after page update because layout might shift
      requestAnimationFrame(() => {
        this.checkOverflow();
      });

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      // Keep History entries same-origin + pretty (path only).
      history.replaceState({ page: "product" }, "", productPath);

      if (newDrawerImg) {
        document.querySelectorAll(".product-configurator-option__drawer-main-image-wrapper img")?.forEach((img) => {
          img.src = newDrawerImg;
        });
      }

      return { inserted, removed };
    } catch (err) {
      console.error("updatePageContent error:", err);
      throw err;
    }
  }
}

customElements.define("product-configurator", ProductConfigurator);
